import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, MapPin, Calendar, Loader2 } from 'lucide-react';

const MyPetsPage = () => {
  const { token } = useAuth();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMyPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/meus-pets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPets(response.data);
    } catch (error) {
      console.error('Error fetching my pets:', error);
      toast.error('Erro ao carregar seus pets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyPets();
  }, [token]);

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/pets/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPets(pets.filter(p => p.id !== deleteId));
      toast.success('Pet removido com sucesso');
    } catch (error) {
      console.error('Error deleting pet:', error);
      toast.error('Erro ao remover pet');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const statusColors = {
    perdido: 'bg-[#E57373]',
    encontrado: 'bg-[#81C784]',
    adocao: 'bg-[#64B5F6]'
  };

  const statusLabels = {
    perdido: 'Perdido',
    encontrado: 'Encontrado',
    adocao: 'Para Adoção'
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-display font-bold text-[#5D4A3A]">Meus Pets</h1>
              <p className="text-[#7A5B3E] mt-1">Gerencie os pets que você cadastrou</p>
            </div>
            <Link to="/cadastrar-pet">
              <Button className="btn-primary">
                <Plus className="w-5 h-5 mr-2" />
                Novo Pet
              </Button>
            </Link>
          </div>

          {/* Pet List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-[#FCEBC5] rounded-2xl h-32 animate-pulse border border-[#C4956A]"></div>
              ))}
            </div>
          ) : pets.length > 0 ? (
            <div className="space-y-4">
              {pets.map(pet => (
                <div 
                  key={pet.id} 
                  className="bg-[#FCEBC5] rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-[#C4956A]"
                  data-testid={`my-pet-${pet.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                      {pet.imagem_url ? (
                        <img 
                          src={pet.imagem_url} 
                          alt={pet.nome}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#F5E6C8] flex items-center justify-center">
                          <span className="text-5xl">🐾</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-[#5D4A3A]">{pet.nome}</h3>
                            <Badge className={`${statusColors[pet.status]} text-white rounded-full`}>
                              {statusLabels[pet.status]}
                            </Badge>
                          </div>
                          <p className="text-[#6B5344] font-medium">
                            {pet.especie} {pet.raca && `• ${pet.raca}`}
                          </p>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => setDeleteId(pet.id)}
                          data-testid={`delete-pet-${pet.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>

                      <p className="text-[#7A5B3E] mt-2 line-clamp-2">{pet.descricao}</p>

                      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#7A5B3E]">
                        {pet.ponto_referencia && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{pet.ponto_referencia}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Cadastrado em {formatDate(pet.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#FCEBC5] rounded-2xl shadow-lg border border-[#C4956A]">
              <span className="text-6xl">🐾</span>
              <h3 className="text-xl font-bold text-[#5D4A3A] mt-4 mb-2">Você ainda não cadastrou nenhum pet</h3>
              <p className="text-[#7A5B3E] mb-6">Comece cadastrando um pet perdido, encontrado ou para adoção</p>
              <Link to="/cadastrar-pet">
                <Button className="btn-primary">
                  <Plus className="w-5 h-5 mr-2" />
                  Cadastrar Meu Primeiro Pet
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#FCEBC5] border-[#C4956A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#5D4A3A]">Remover Pet</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A5B3E]">
              Tem certeza que deseja remover este pet? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="rounded-full border-[#C4956A] text-[#5D4A3A]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 rounded-full"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Removendo...</>
              ) : (
                'Remover'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default MyPetsPage;
